import { useCallback, useRef } from "react";

import { usePaneManager } from "./store";

/**
 * 画面ごとに控える「最後に見ていた場所」。
 * `anchor` は**どの行から出たか**で、行を押して次の画面へ移ったときだけ入る。
 */
type Spot = { top: number; anchor: string | null };

/**
 * 画面の識別子 → 控え。
 *
 * **捨てる後始末は置かない。** 識別子は `nextUid()` が単調に発行して再利用されないため、閉じた
 * 画面の控えが別の画面へ当たることは無い。1 件あたり数十バイトで、1 セッションの画面数はたかが
 * 知れている。捨てるために `store.ts` を触るほうが、得るものに対して構造を増やす。
 */
const spots = new Map<string, Spot>();

/** 出所の行を探す。CSS.escape に頼らず走査する（識別子に引用符が混じっても壊れない）。 */
function findRow(box: HTMLElement, anchor: string): HTMLElement | null {
    const rows = box.querySelectorAll<HTMLElement>("[data-anchor]");
    for (let i = 0; i < rows.length; i += 1) {
        const row = rows[i];
        if (row && row.getAttribute("data-anchor") === anchor) return row;
    }
    return null;
}

/**
 * 控えた場所へ戻す。行を名指ししていればその行を枠の中央へ置き、出所として一瞬色を残す。
 * 行が見つからない（離れている間に消えた）ときは控えたスクロール量へ落とす。
 */
function restore(box: HTMLElement, spot: Spot): void {
    const row = spot.anchor === null ? null : findRow(box, spot.anchor);
    if (!row) {
        box.scrollTop = spot.top;
        return;
    }
    const boxTop = box.getBoundingClientRect().top;
    const rect = row.getBoundingClientRect();
    // offsetTop は使わない。枠に position が付いておらず、基準が枠自身になる保証が無い。
    box.scrollTop = Math.max(
        0,
        box.scrollTop + (rect.top - boxTop) - (box.clientHeight - rect.height) / 2
    );
    row.classList.add("row-landed");
}

/**
 * 戻ったときに、離れたときの場所へ帰す（[[2026-09-04 設計ノート アドイン 戻ったときの表示位置]]）。
 *
 * アドインは**いま見ている画面 1 つだけ**を描くので、戻ると画面は作り直しになり、スクロール位置は
 * 初期値へ落ちる。位置を覚えている場所が無いだけなので、画面の識別子を鍵に控えてここで書き戻す。
 *
 * 使い方は 2 つ。スクロール枠へ `ref` を渡すのが土台で、これだけで全画面に効く。行を押して次の
 * 画面へ移る画面は、移る直前に `leaveFrom(鍵)` を呼び、その行へ `data-anchor={鍵}` を置く。
 * 戻ったときにその行が枠の中央へ来るので、離れている間に紙面が増減しても目的の行に着く。
 *
 * `ref` はコールバック ref である。枠が DOM へ付いた瞬間（描画前）に書き戻すため、先頭が見えてから
 * 跳ぶちらつきが無い。`paneUid` でメモ化するのは 2 つの理由による。毎描画で作り直すと React が ref を
 * 付け外しして復元が走り続けること、そして**生成担当どうしの行き来では React が同じ実体を使い回す**
 * ため、識別子の変化を捉えて付け直さないと前の担当の位置が残ることである。
 */
export function useScrollMemory() {
    const paneUid = usePaneManager((s) => s.activePaneUid);
    const box = useRef<HTMLElement | null>(null);

    const ref = useCallback(
        (node: HTMLElement | null) => {
            box.current = node;
            if (!node) return undefined;
            const spot = spots.get(paneUid);
            if (spot) restore(node, spot);
            // 利用者が動かしたら控えを更新し、行の名指しは落とす（もうその行から出た話ではない）。
            const onScroll = () => {
                spots.set(paneUid, { top: node.scrollTop, anchor: null });
            };
            node.addEventListener("scroll", onScroll, { passive: true });
            return () => {
                node.removeEventListener("scroll", onScroll);
                box.current = null;
            };
        },
        [paneUid]
    );

    /** この行から次の画面へ移る。いまのスクロール量と行の鍵を控える。 */
    const leaveFrom = useCallback(
        (anchor: string) => {
            spots.set(paneUid, { top: box.current?.scrollTop ?? 0, anchor });
        },
        [paneUid]
    );

    return { ref, leaveFrom };
}
